import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Plus, FolderOpen, ExternalLink, Settings, Trash2, Loader2, Rocket, Globe, Clock, FileCode } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

interface Project {
  id: string; name: string; subdomain: string; status: string;
  file_count: number; storage_bytes: number; deployed_at: number; updated_at: number;
}

export default function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { loadProjects(); }, []);

  async function loadProjects() {
    try { const data = await api.getProjects(); setProjects(data.projects || []); }
    catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }

  async function deleteProject(id: string, name: string) {
    if (!confirm(`Delete "${name}"?`)) return;
    try { await api.deleteProject(id); toast.success('Deleted'); setProjects(p => p.filter(x => x.id !== id)); }
    catch (e: any) { toast.error(e.message); }
  }

  function timeAgo(ts: number): string {
    if (!ts) return 'Never';
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  function formatBytes(b: number): string {
    if (!b) return '0 B';
    if (b < 1024) return b + ' B';
    if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
    return (b / 1048576).toFixed(1) + ' MB';
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">Projects</h1>
          <p className="text-text-muted text-xs md:text-sm mt-0.5">Deploy and manage your static sites</p>
        </div>
        <Link to="/new" className="btn-primary flex items-center gap-2 text-sm">
          <Plus size={16} /> New Project
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1,2,3].map(i => <div key={i} className="card animate-pulse h-36"><div className="h-4 bg-border rounded w-3/4 mb-3" /><div className="h-3 bg-border rounded w-1/2" /></div>)}
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20">
          <FolderOpen size={40} className="mx-auto mb-3 text-text-muted" />
          <h2 className="font-semibold mb-1">No projects yet</h2>
          <p className="text-text-muted text-sm mb-4">Create your first project and deploy in seconds.</p>
          <Link to="/new" className="btn-primary inline-flex items-center gap-2"><Rocket size={14} /> Create Project</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="card group cursor-pointer hover:border-accent/30 transition-all"
              onClick={() => navigate(`/project/${p.id}`)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
                    <FolderOpen size={14} className="text-accent" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm truncate">{p.name}</h3>
                    <p className="text-[11px] text-text-muted truncate">{p.subdomain}.meganhost.dpdns.org</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Link to={`/project/${p.id}/editor`} onClick={e => e.stopPropagation()} className="p-1 rounded hover:bg-elevated text-text-muted hover:text-accent" title="Open Editor"><FileCode size={13} /></Link>
                  <Link to={`/project/${p.id}/settings`} onClick={e => e.stopPropagation()} className="p-1 rounded hover:bg-elevated text-text-muted" title="Settings"><Settings size={13} /></Link>
                  <button onClick={e => { e.stopPropagation(); deleteProject(p.id, p.name); }} className="p-1 rounded hover:bg-elevated text-text-muted hover:text-danger" title="Delete"><Trash2 size={13} /></button>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`badge text-[10px] ${p.status === 'deployed' ? 'badge-success' : 'badge-muted'}`}>{p.status}</span>
                <span className="text-[11px] text-text-muted flex items-center gap-1"><FileCode size={11} /> {p.file_count} files</span>
                {p.storage_bytes > 0 && <span className="text-[11px] text-text-muted">{formatBytes(p.storage_bytes)}</span>}
              </div>

              <div className="flex items-center justify-between text-[10px] text-text-muted pt-2 border-t border-border">
                <span className="flex items-center gap-1"><Clock size={10} /> {p.deployed_at ? timeAgo(p.deployed_at) : 'Not deployed'}</span>
                <a href={`https://${p.subdomain}.meganhost.dpdns.org`} target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="flex items-center gap-1 text-accent hover:underline">
                  <Globe size={10} /> Visit
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
